import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, ClipboardList, CheckSquare, User, GraduationCap, Phone, Mail, History, TrendingUp, FileText, Download, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { openBlobInNewTab } from '../utils/fileUtils';
import { useDocumentPreview } from '../components/DocumentPreviewModal';
import ZenButton from '../components/ZenButton';
import EvolutionNotesChart from '../components/EvolutionNotesChart';
import NotificationWidget from '../components/NotificationWidget';
import StagiaireAccordionCardFormateur from '../components/formateur/StagiaireAccordionCardFormateur';
import { Button } from '../components/ui/button';
import { BandeauCharteASigner } from '../components/charte-formateur/BandeauCharteASigner';
import { TuileGuide } from '../components/guide-bc/TuileGuide';
import { TuileLivretBase } from '../components/livret-base/TuileLivretBase';

const DashboardFormateur = ({ user }) => {
  const { previewDocument, DocumentModal } = useDocumentPreview();
  const [stagiaires, setStagiaires] = useState([]);
  const [stagiairesList, setStagiairesList] = useState([]);
  const [documentsFISL, setDocumentsFISL] = useState([]);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [profile, setProfile] = useState({
    photo: '',
    diplome: '',
    annee_diplome: '',
    telephone: user.telephone || '',
    email: user.email || '',
  });

  useEffect(() => {
    loadStagiaires();
    loadProfile();
    loadDocumentsFISL();
  }, []);

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      setStagiaires(response.data || []);
      const formattedList = (response.data || []).map(s => ({
        user_id: s.user_id,
        prenom: s.user_info?.prenom || s.prenom || '',
        nom: s.user_info?.nom || s.nom || ''
      }));
      setStagiairesList(formattedList);
    } catch (error) {
    }
  };

  const loadProfile = async () => {
    try {
      const response = await axios.get(`/formateurs/${user.user_id}`);
      if (response.data.profile) {
        setProfile(prev => ({ ...prev, ...response.data.profile }));
      }
    } catch (error) {
    }
  };

  const loadDocumentsFISL = async () => {
    try {
      const response = await axios.get('/documents-formateur');
      setDocumentsFISL(response.data || []);
    } catch (error) {
      setDocumentsFISL([]);
    }
  };

  const handleViewDocument = async (doc) => {
    await previewDocument(doc.document_id, doc.filename, `/documents-formateur/${doc.document_id}/download`);
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const response = await axios.get(`/documents-formateur/${doc.document_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      loadDocumentsFISL();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-formateur">
      <BandeauCharteASigner />
      <TuileLivretBase user={user} />
      {/* Header Bandeau Bleu */}
      <div className="bg-gradient-to-r from-[#3B82F6] to-[#1D4ED8] rounded-xl p-6 text-white shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center gap-6">
          <div className="flex-shrink-0">
            <div className="w-24 h-32 bg-white/20 border-4 border-white/40 rounded-lg overflow-hidden">
              {profile.photo ? (
                <img src={profile.photo} alt="Photo" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="h-12 w-12 text-white/60" />
                </div>
              )}
            </div>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{user.prenom} {user.nom}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/90 mb-3">
              {profile.telephone && (
                <span className="flex items-center gap-1"><Phone className="h-4 w-4" />{profile.telephone}</span>
              )}
              <span className="flex items-center gap-1"><Mail className="h-4 w-4" />{profile.email || user.email}</span>
            </div>
            <div className="bg-white/10 rounded-lg px-4 py-3">
              <p className="font-semibold flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                {profile.diplome || 'Diplôme non renseigné'}
                {profile.annee_diplome && ` (${profile.annee_diplome})`}
              </p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="bg-white/10 rounded-lg px-4 py-3 text-center">
              <p className="text-3xl font-bold">{stagiaires.length}</p>
              <p className="text-sm text-white/80">Stagiaires</p>
            </div>
          </div>
        </div>
      </div>

      {/* Zen Button */}
      <div className="flex justify-end gap-2">
        <a href="https://www.legifrance.gouv.fr/jorf/id/JORFTEXT000051901186" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white shadow-lg hover:shadow-xl transition-all duration-300" data-testid="referentiel-diplome-btn">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20"/></svg>
          <span className="hidden sm:inline">Référentiel diplôme</span>
          <span className="sm:hidden">Réf.</span>
        </a>
        <ZenButton />
      </div>

      <TuileGuide />

      {/* Actions rapides */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <a href="/presence" className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm border-l-4 border-l-blue-500 hover:shadow-md transition-shadow" data-testid="action-presence">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-500/10 p-2 rounded-lg">
              <ClipboardList className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="font-semibold text-[#0F172A]">Feuille de Présence</p>
              <p className="text-xs text-slate-600">Matin / Après-midi</p>
            </div>
          </div>
        </a>

        <a href="/notation" className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm border-l-4 border-l-purple-500 hover:shadow-md transition-shadow" data-testid="action-notation">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-500/10 p-2 rounded-lg">
              <CheckSquare className="h-5 w-5 text-purple-500" />
            </div>
            <div>
              <p className="font-semibold text-[#0F172A]">Noter les Stagiaires</p>
              <p className="text-xs text-slate-600">Épreuves formatives</p>
            </div>
          </div>
        </a>

        <a href="/historique-notes" className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm border-l-4 border-l-orange-500 hover:shadow-md transition-shadow" data-testid="action-historique">
          <div className="flex items-center space-x-3">
            <div className="bg-orange-500/10 p-2 rounded-lg">
              <History className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="font-semibold text-[#0F172A]">Historique Notes</p>
              <p className="text-xs text-slate-600">Mes évaluations</p>
            </div>
          </div>
        </a>

        {/* Documents FISL Card */}
        <div
          onClick={() => setShowDocumentsModal(true)}
          className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm border-l-4 border-l-emerald-500 cursor-pointer hover:shadow-md transition-shadow relative"
          data-testid="action-documents-fisl"
        >
          {documentsFISL.filter(d => !d.lu).length > 0 && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center shadow-lg">
              {documentsFISL.filter(d => !d.lu).length}
            </div>
          )}
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-500/10 p-2 rounded-lg">
              <FileText className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <p className="font-semibold text-[#0F172A]">Documents FISL</p>
              <p className="text-xs text-slate-600">{documentsFISL.length} document{documentsFISL.length > 1 ? 's' : ''}</p>
            </div>
          </div>
        </div>

        <NotificationWidget />
      </div>

      {/* Modal Documents FISL */}
      {showDocumentsModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-emerald-50">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-500/20 p-2 rounded-lg">
                  <FileText className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#0F172A]">Documents envoyés par le Centre FISL</h3>
                  <p className="text-sm text-slate-500">{documentsFISL.length} document{documentsFISL.length > 1 ? 's' : ''}</p>
                </div>
              </div>
              <button onClick={() => setShowDocumentsModal(false)} className="text-slate-400 hover:text-slate-600 p-1">
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {documentsFISL.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Aucun document reçu du centre</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documentsFISL.map((doc, index) => (
                    <div
                      key={doc.document_id || index}
                      className={`flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors ${
                        !doc.lu ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${!doc.lu ? 'bg-emerald-200' : 'bg-emerald-100'}`}>
                          <FileText className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-[#0F172A]">{doc.titre || doc.filename}</p>
                            {!doc.lu && <span className="text-xs px-2 py-0.5 bg-emerald-500 text-white rounded-full">Nouveau</span>}
                          </div>
                          <p className="text-sm text-slate-500">{doc.description || 'Document'} {doc.created_at && `\u2022 ${new Date(doc.created_at).toLocaleDateString('fr-FR')}`}</p>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-3">
                        <Button size="sm" variant="outline" onClick={() => handleViewDocument(doc)} className="text-blue-600 border-blue-200 hover:bg-blue-50" title="Voir">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDownloadDocument(doc)} className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" title="Télécharger">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <Button onClick={() => setShowDocumentsModal(false)} className="w-full bg-slate-600 hover:bg-slate-700">Fermer</Button>
            </div>
          </div>
        </div>
      )}

      {/* Mes Stagiaires - Accordéon */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <Users className="h-6 w-6 text-[#3B82F6]" />
          <h2 className="text-2xl font-display font-semibold text-[#0F172A]">Mes Stagiaires</h2>
          <span className="text-sm text-slate-400">Cliquez pour voir profil, EPMSP et compétences</span>
        </div>
        {stagiaires.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucun stagiaire assigné</p>
          </div>
        ) : (
          <div className="space-y-3">
            {stagiaires.map((stagiaire, index) => (
              <StagiaireAccordionCardFormateur
                key={stagiaire.user_id}
                stagiaire={stagiaire}
                index={index}
              />
            ))}
          </div>
        )}
      </div>

      {/* Evolution Notes */}
      <EvolutionNotesChart formateurId={user.user_id} stagiaires={stagiairesList} />
      <DocumentModal />
    </div>
  );
};

export default DashboardFormateur;
